import { Helmet } from 'react-helmet'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { QueryParamProvider } from 'use-query-params'
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6'
import { ConfigProvider } from 'antd'

import './index.css'
import { AuthWrapper } from 'admin/lib/Api'
import { ShortcutsProvider } from './lib/shortcuts'

import {
  Accounts,
  Account,
  BIReports,
  BIReportEdit,
  Certificate,
  Certificates,
  Connections,
  Connection,
  CompanyEdit,
  CompanyRegister,
  Companies,
  Companies_v2,
  EditPublicApiKey,
  EditReferenceProductItem,
  DevelopmentPlayground,
  FullscreenImage,
  GroupUpdate,
  GroupUpdateLog,
  InTray,
  Notification,
  Notifications,
  OneClickLca,
  Procore,
  ProjectPage,
  Projects,
  PublicApiKeys,
  RecordEditWrapper,
  RecordEditWrapper_v2,
  RecordsSummary,
  ReferenceDataIndex,
  ReferenceProductItems,
  SignOut,
  SignInOidc,
  SmartWaste,
  TemplateGui,
  Users,
  UserEdit
} from './pages'
import * as ROUTES from './routes'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchInterval: false,
      staleTime: 30000
    }
  }
})

export function App() {
  return (
    <AuthWrapper>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: '#22aeb5',
            colorInfo: '#22aeb5',
            colorSuccess: '#016549',
            colorWarning: '#f3a203',
            colorError: '#b20a01',
            colorText: '#000000',
            colorTextBase: '#000000',
            borderRadius: 5,
            boxShadow:
              '0 3px 6px -4px rgba(255, 255, 255, 0.12), 0 6px 16px 0 rgba(255, 255, 255, 0.08), 0 9px 28px 8px rgba(255, 255, 255, 0.05)'
          }
        }}
      >
        <QueryClientProvider client={queryClient}>
          <ShortcutsProvider>
            <BrowserRouter>
              <QueryParamProvider adapter={ReactRouter6Adapter}>
                <Routes>
                  <Route
                    path={ROUTES.SIGNIN_OIDC}
                    element={
                      <>
                        <Helmet>
                          <title>Signing In...</title>
                        </Helmet>
                        <SignInOidc />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.SIGNOUT}
                    element={
                      <>
                        <Helmet>
                          <title>Signing Out</title>
                        </Helmet>
                        <SignOut />
                      </>
                    }
                  />

                  <Route
                    path={ROUTES.INTRAY}
                    element={
                      <>
                        <Helmet>
                          <title>InTray</title>
                        </Helmet>
                        <InTray />
                      </>
                    }
                  />

                  <Route
                    path={ROUTES.RECORDS_SUMMARY.LIST}
                    element={
                      <>
                        <Helmet>
                          <title>Records Summary</title>
                        </Helmet>
                        <RecordsSummary />
                      </>
                    }
                  />

                  <Route
                    path={ROUTES.GROUP_UPDATE.EDIT}
                    element={
                      <>
                        <Helmet>
                          <title>Qupid</title>
                        </Helmet>
                        <GroupUpdate />
                      </>
                    }
                  />

                  <Route
                    path={ROUTES.GROUP_UPDATE.LOG}
                    element={
                      <>
                        <Helmet>
                          <title>Qupid Log</title>
                        </Helmet>
                        <GroupUpdateLog />
                      </>
                    }
                  />

                  <Route
                    path={ROUTES.RECORD.EDIT}
                    element={
                      <>
                        <Helmet>
                          <title>Edit</title>
                        </Helmet>
                        <RecordEditWrapper />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.RECORD_V2.EDIT}
                    element={
                      <>
                        <Helmet>
                          <title>Edit</title>
                        </Helmet>
                        <RecordEditWrapper_v2 />
                      </>
                    }
                  />

                  <Route
                    path={ROUTES.IMAGE}
                    element={
                      <>
                        <Helmet>
                          <title>Image</title>
                        </Helmet>
                        <FullscreenImage />
                      </>
                    }
                  />

                  <Route
                    path={ROUTES.REFERENCEITEMS.LIST}
                    element={
                      <>
                        <Helmet>
                          <title>Reference Product Items</title>
                        </Helmet>
                        <ReferenceProductItems />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.REFERENCEITEMS.EDIT}
                    element={
                      <>
                        <Helmet>
                          <title>Edit Reference Product Item</title>
                        </Helmet>
                        <EditReferenceProductItem />
                      </>
                    }
                  />

                  <Route
                    path={ROUTES.CERTIFICATES.LIST}
                    element={
                      <>
                        <Helmet>
                          <title>Certificates</title>
                        </Helmet>
                        <Certificates />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.CERTIFICATES.EDIT}
                    element={
                      <>
                        <Helmet>
                          <title>Edit Certificate</title>
                        </Helmet>
                        <Certificate />
                      </>
                    }
                  />

                  <Route
                    path={ROUTES.COMPANIES.LIST}
                    element={
                      <>
                        <Helmet>
                          <title>Companies</title>
                        </Helmet>
                        <Companies />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.COMPANIES.LIST_V2}
                    element={
                      <>
                        <Helmet>
                          <title>Suppliers and Manufacturers</title>
                        </Helmet>
                        <Companies_v2 />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.COMPANIES.NEW}
                    element={
                      <>
                        <Helmet>
                          <title>New Company</title>
                        </Helmet>
                        <CompanyRegister />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.COMPANIES.EDIT}
                    element={
                      <>
                        <Helmet>
                          <title>Edit Company</title>
                        </Helmet>
                        <CompanyEdit />
                      </>
                    }
                  />

                  <Route
                    path={ROUTES.REFERENCE_DATA.INDEX}
                    element={
                      <>
                        <Helmet>
                          <title>Reference Data</title>
                        </Helmet>
                        <ReferenceDataIndex />
                      </>
                    }
                  />

                  <Route
                    path={ROUTES.PUBLIC_API_KEYS.LIST}
                    element={
                      <>
                        <Helmet>
                          <title>Public API Keys</title>
                        </Helmet>
                        <PublicApiKeys />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.PUBLIC_API_KEYS.EDIT}
                    element={
                      <>
                        <Helmet>
                          <title>Edit Public API Key</title>
                        </Helmet>
                        <EditPublicApiKey />
                      </>
                    }
                  />

                  <Route
                    path={ROUTES.TEMPLATE_GUI.INDEX}
                    element={
                      <>
                        <Helmet>
                          <title>Templates</title>
                        </Helmet>
                        <TemplateGui />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.USERS.LIST}
                    element={
                      <>
                        <Helmet>
                          <title>Users</title>
                        </Helmet>
                        <Users />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.USERS.EDIT}
                    element={
                      <>
                        <Helmet>
                          <title>Edit User</title>
                        </Helmet>
                        <UserEdit />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.SMART_WASTE.EDIT}
                    element={
                      <>
                        <Helmet>
                          <title>SmartWaste Integration</title>
                        </Helmet>
                        <SmartWaste />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.ONECLICK_LCA.EDIT}
                    element={
                      <>
                        <Helmet>
                          <title>One Click LCA Integration</title>
                        </Helmet>
                        <OneClickLca />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.PROCORE.EDIT}
                    element={
                      <>
                        <Helmet>
                          <title>Procore Integration</title>
                        </Helmet>
                        <Procore />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.BI_REPORTS.INDEX}
                    element={
                      <>
                        <Helmet>
                          <title>BIReports</title>
                        </Helmet>
                        <BIReports />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.BI_REPORTS.EDIT}
                    element={
                      <>
                        <Helmet>
                          <title>BIReports Edit</title>
                        </Helmet>
                        <BIReportEdit />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.DEVELOPMENT.PLAYGROUND}
                    element={
                      <>
                        <Helmet>
                          <title>Playground</title>
                        </Helmet>
                        <DevelopmentPlayground />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.ACCOUNTS.LIST}
                    element={
                      <>
                        <Helmet>
                          <title>Accounts</title>
                        </Helmet>
                        <Accounts />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.ACCOUNTS.EDIT}
                    element={
                      <>
                        <Helmet>
                          <title>Account Edit</title>
                        </Helmet>
                        <Account />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.ACCOUNTS.NEW}
                    element={
                      <>
                        <Helmet>
                          <title>New Account</title>
                        </Helmet>
                        <Account />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.PROJECTS.LIST}
                    element={
                      <>
                        <Helmet>
                          <title>Projects</title>
                        </Helmet>
                        <Projects />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.PROJECTS.EDIT}
                    element={
                      <>
                        <Helmet>
                          <title>Project Edit</title>
                        </Helmet>
                        <ProjectPage />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.PROJECTS.NEW}
                    element={
                      <>
                        <Helmet>
                          <title>New Project</title>
                        </Helmet>
                        <ProjectPage />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.CONNECTIONS.LIST}
                    element={
                      <>
                        <Helmet>
                          <title>Connections</title>
                        </Helmet>
                        <Connections />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.CONNECTIONS.EDIT}
                    element={
                      <>
                        <Helmet>
                          <title>Connection Edit</title>
                        </Helmet>
                        <Connection />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.CONNECTIONS.NEW}
                    element={
                      <>
                        <Helmet>
                          <title>New Connection</title>
                        </Helmet>
                        <Connection />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.NOTIFICATIONS.LIST}
                    element={
                      <>
                        <Helmet>
                          <title>Notifications</title>
                        </Helmet>
                        <Notifications />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.NOTIFICATIONS.EDIT}
                    element={
                      <>
                        <Helmet>
                          <title>Notification Edit</title>
                        </Helmet>
                        <Notification />
                      </>
                    }
                  />
                  <Route
                    path={ROUTES.NOTIFICATIONS.NEW}
                    element={
                      <>
                        <Helmet>
                          <title>New Notification</title>
                        </Helmet>
                        <Notification />
                      </>
                    }
                  />

                  <Route path="/" element={<Navigate to={ROUTES.INTRAY} />} />

                  <Route
                    path="*"
                    element={
                      <>
                        <Helmet>
                          <title>404</title>
                        </Helmet>
                        <div>404</div>
                      </>
                    }
                  />
                </Routes>
              </QueryParamProvider>
            </BrowserRouter>
          </ShortcutsProvider>
        </QueryClientProvider>
      </ConfigProvider>
    </AuthWrapper>
  )
}
